import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faArrowUpRightFromSquare, faChevronDown, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useMemo, useState } from 'react';
import { SuggestedTermIcon, useTaxonomyTermName } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import {
  AuthoritySourceIndex,
  CitationHostRowLike,
  CitationSourceGroup,
  groupCitationHosts,
  resolveSourceLabel,
  SourceGrouping,
  UNCURATED_SOURCE_HINT,
} from './citationSources.utils';
import { CitationLike, orderSourceEntities, selectSourceCitations } from './GovernanceAiAnswerDetail.utils';
import { SourceFavicon, FAVICON_SIZE } from './SourceFavicon';
import { SourceGroupingToggle } from './SourceChips';
import { readableTextColor } from './governanceThemeColors';
import { termStatusPresentation } from './termStatusPresentation';

/** The colour a chip falls back to when the entity is neither the customer nor a tracked rival. */
const UNTRACKED_ENTITY_COLOR = '#64748b';

/** How many entity chips one source row shows before the rest collapse into a count. */
const ENTITY_CHIP_LIMIT = 4;

/** Why a source row has no entity chips at all. */
const INDEPENDENT_SOURCE_HINT = 'Cited without being tied to any brand you track.';

/** Said once above the rows, so the count beside each source is never guessed at. */
const CITED_SOURCES_CAPTION = 'Where this answer got its information. The number is how often the answer leaned on each site.';

/**
 * One entity a source was cited for.
 *
 * The colour is the one this entity carries everywhere else on the page — the answer tints, the
 * legend, "Who's mentioned" — because a reader who has learnt a competitor's colour in the answer
 * body must meet the same colour here. An entity outside the tracked set takes no colour: tinting
 * it would imply a relationship the project never declared.
 */
function SourceEntityChip({
  api,
  termKey,
  citations,
  label,
  color,
  isStoredColor,
}: {
  api?: ISyncCoreApi;
  termKey: string;
  citations: number;
  /** The page-level label, where the entity is one the run measured. "You" for own-side entities. */
  label?: string;
  color?: string;
  /** Whether `color` is a stored brand colour — the ones darkened for a readable label. */
  isStoredColor?: boolean;
}) {
  // Only consulted when the page could not label the entity itself — the mirror can credit a
  // citation to a term the run's own entity list does not carry.
  const { termName, termStatus, loading } = useTaxonomyTermName(api, label ? '' : termKey);
  const presentation = termStatusPresentation(termStatus, 'badge');

  if (presentation.hidden) {
    return null;
  }

  if (!label && loading) {
    // A term key on screen is a defect; the chip waits for its name instead.
    return (
      <span className="inline-block h-3 w-14 rounded bg-slate-100 animate-pulse" aria-label="Loading brand name" role="presentation" />
    );
  }

  // The shared hook answers with the KEY when it cannot resolve a name, so anything equal to the
  // key is "unnamed", not a name. A chip that cannot say which brand it is conveys nothing, so it
  // is dropped rather than rendered as an identifier. Own-side entities are always labelled by the
  // page, so "You" can never be the one that disappears.
  const resolvedLabel = label ?? (termName && termName !== termKey ? termName : undefined);
  if (!resolvedLabel) {
    return null;
  }

  const swatch = color ?? UNTRACKED_ENTITY_COLOR;
  // The swatch is the raw colour; only a stored colour is darkened so the label stays legible.
  const labelColor = color && isStoredColor ? readableTextColor(color) : swatch;

  return (
    <span
      className={['flex items-center gap-1 text-[11px]', presentation.className].filter(Boolean).join(' ')}
      title={presentation.title ?? `${resolvedLabel} · cited ${citations} ${citations === 1 ? 'time' : 'times'}`}
    >
      <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: swatch }} aria-hidden />
      <span className="max-w-[8rem] truncate" style={{ color: labelColor, fontWeight: 600 }}>
        {resolvedLabel}
      </span>
      {presentation.showSuggestionIcon ? <SuggestedTermIcon /> : null}
      <span className="tabular-nums text-slate-400">{citations}</span>
    </span>
  );
}

/**
 * The "Sources cited" block on the answer detail page.
 *
 * Collapsed, it renders PURELY from the run's own `citationHosts` mirror — so it is on screen with
 * the rest of the run document, before (and whether or not) the parsed answer file arrives.
 * Expanding a source lists the individual pages behind it from the parsed document the page has
 * already downloaded for the answer body; nothing here issues a request of its own.
 *
 * The analyst-grade Citations tab under "Analysis details" is untouched — this is the reading
 * surface, that one is the audit trail.
 */
export function AnswerCitedSources({
  api,
  citationHosts,
  citations,
  parsedLoading,
  parsedFailed,
  grouping,
  onGroupingChange,
  authoritySources,
  sourcesReady,
  colorByTermKey,
  storedTermKeys,
  labelByTermKey,
  ownTermKeys,
}: {
  api?: ISyncCoreApi;
  citationHosts?: readonly CitationHostRowLike[] | null;
  citations?: readonly CitationLike[];
  /** True while the parsed document is still on its way. */
  parsedLoading: boolean;
  /** True when it will not arrive at all. */
  parsedFailed: boolean;
  grouping: SourceGrouping;
  onGroupingChange: (next: SourceGrouping) => void;
  authoritySources: AuthoritySourceIndex;
  sourcesReady: boolean;
  /** The page's one colour assignment, keyed by taxonomy term. */
  colorByTermKey: Map<string, string>;
  /** Which of those colours are a stored brand colour — the ones darkened for a readable label. */
  storedTermKeys: ReadonlySet<string>;
  /** The page's labels, keyed by taxonomy term. Own-side entities all read "You". */
  labelByTermKey: Map<string, string>;
  /** Which of those terms are the customer's own, so "You" can lead its row. */
  ownTermKeys: ReadonlySet<string>;
}) {
  const groups = useMemo(() => groupCitationHosts(citationHosts, grouping), [citationHosts, grouping]);
  /**
   * Which sources the reader has collapsed — the inverse of what this tracked before.
   *
   * Every source starts EXPANDED. Someone reading an answer wants to see which pages it leaned on,
   * and making them click each row to find out turned the block into a set of doors rather than a
   * list of sources. Collapsing stays available per row; it is simply no longer the price of
   * admission. Tracking the collapsed set rather than the expanded one is what makes "expanded"
   * the default without having to seed state from the group list on every regrouping.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());

  // A run whose mirror never arrived has not been measured for sources at all. Saying "0 sources"
  // would claim a measurement; the block simply does not appear, exactly as it did not before.
  if (!citationHosts || !groups.length) {
    return null;
  }

  const toggle = (key: string) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  return (
    <div className="rounded-xl bg-white p-4 ring-1 ring-inset ring-slate-200/80">
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
          Sources cited ({groups.length})
        </div>
        <SourceGroupingToggle grouping={grouping} onChange={onGroupingChange} />
      </div>
      <div className="text-[11px] text-slate-400 mb-2">{CITED_SOURCES_CAPTION}</div>

      {/* As long as its sources: the page scrolls the list, not a box of its own. */}
      <div className="space-y-2">
        {groups.map((group) => (
          <CitedSourceRow
            key={group.key}
            api={api}
            group={group}
            citations={citations}
            parsedLoading={parsedLoading}
            parsedFailed={parsedFailed}
            authoritySources={authoritySources}
            sourcesReady={sourcesReady}
            colorByTermKey={colorByTermKey}
            storedTermKeys={storedTermKeys}
            labelByTermKey={labelByTermKey}
            ownTermKeys={ownTermKeys}
            open={!collapsed.has(group.key)}
            onToggle={() => toggle(group.key)}
          />
        ))}
      </div>
    </div>
  );
}

function CitedSourceRow({
  api,
  group,
  citations,
  parsedLoading,
  parsedFailed,
  authoritySources,
  sourcesReady,
  colorByTermKey,
  storedTermKeys,
  labelByTermKey,
  ownTermKeys,
  open,
  onToggle,
}: {
  api?: ISyncCoreApi;
  group: CitationSourceGroup;
  citations?: readonly CitationLike[];
  parsedLoading: boolean;
  parsedFailed: boolean;
  authoritySources: AuthoritySourceIndex;
  sourcesReady: boolean;
  colorByTermKey: Map<string, string>;
  storedTermKeys: ReadonlySet<string>;
  labelByTermKey: Map<string, string>;
  ownTermKeys: ReadonlySet<string>;
  open: boolean;
  onToggle: () => void;
}) {
  const resolved = resolveSourceLabel(group.key, authoritySources);
  const entities = useMemo(() => orderSourceEntities(group.entities, (termKey) => ownTermKeys.has(termKey)), [group.entities, ownTermKeys]);
  const pages = useMemo(() => (open ? selectSourceCitations(citations, group) : []), [open, citations, group]);

  const shownEntities = entities.slice(0, ENTITY_CHIP_LIMIT);
  const hiddenEntities = entities.length - shownEntities.length;

  return (
    <div>
      <button
        type="button"
        className="w-full text-left flex items-center gap-2 border-0 bg-transparent p-0"
        onClick={onToggle}
        aria-expanded={open}
      >
        <FontAwesomeIcon icon={open ? faChevronDown : faChevronRight} className="text-slate-400 text-[10px] flex-shrink-0" aria-hidden />
        <SourceFavicon api={api} domain={group.key} />
        {sourcesReady ? (
          <span
            className={['text-xs font-semibold text-slate-700 truncate', resolved.presentation.className].filter(Boolean).join(' ')}
            title={resolved.presentation.title ?? (resolved.isUncurated ? UNCURATED_SOURCE_HINT : resolved.label)}
          >
            {resolved.label}
          </span>
        ) : (
          // Held until the curated names land, so a source never reads as its hostname and then as
          // its name within one page load.
          <span className="inline-block h-4 w-28 rounded bg-slate-100 animate-pulse" aria-label="Loading source name" role="presentation" />
        )}
        {resolved.presentation.showSuggestionIcon ? <SuggestedTermIcon spacing="flush" /> : null}
        <span className="text-xs tabular-nums text-slate-500 flex-shrink-0">{group.citations}</span>
        <span className="flex items-center gap-2 flex-wrap ml-auto">
          {entities.length ? (
            <>
              {shownEntities.map((entity) => (
                <SourceEntityChip
                  key={entity.termKey}
                  api={api}
                  termKey={entity.termKey}
                  citations={entity.citations}
                  label={labelByTermKey.get(entity.termKey)}
                  color={colorByTermKey.get(entity.termKey)}
                  isStoredColor={storedTermKeys.has(entity.termKey)}
                />
              ))}
              {hiddenEntities > 0 ? <span className="text-[11px] text-slate-400">+{hiddenEntities}</span> : null}
            </>
          ) : (
            // An unattributed source is a finding, not a gap: it is what makes the source
            // independent of every brand the project tracks.
            <span className="text-[11px] text-slate-400" title={INDEPENDENT_SOURCE_HINT}>
              Independent
            </span>
          )}
        </span>
      </button>

      {open ? (
        <div className="ps-5 pt-1 space-y-1">
          {pages.length ? (
            pages.map((entry, index) => (
              <div key={`${entry.citation.url}-${index}`} className="flex items-baseline gap-2 text-xs min-w-0">
                <a
                  href={entry.citation.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-700 hover:underline truncate flex-shrink-0 max-w-[60%]"
                  title={entry.label === entry.citation.url ? entry.citation.url : `${entry.label} — ${entry.citation.url}`}
                >
                  {entry.label}
                  <FontAwesomeIcon
                    icon={faArrowUpRightFromSquare}
                    className="text-slate-400 ms-1"
                    style={{ width: FAVICON_SIZE, height: FAVICON_SIZE }}
                    aria-hidden
                  />
                </a>
                {/* The address, muted and secondary — the title is what the reader picks a link by. */}
                <span className="text-slate-400 truncate min-w-0">{entry.citation.url}</span>
              </div>
            ))
          ) : parsedFailed ? (
            <div className="text-xs text-slate-400">The individual pages could not be loaded for this answer.</div>
          ) : parsedLoading ? (
            <div className="h-4 w-56 rounded bg-slate-100 animate-pulse" aria-label="Loading cited pages" role="presentation" />
          ) : (
            <div className="text-xs text-slate-400">No individual pages were recorded for this source.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
